import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import DOMPurify from "dompurify";
import { PublicShell } from "@/components/PublicShell";
import { api } from "@/lib/api";
import { photoSrc } from "@/lib/uploads";
import { Calendar, User as UserIcon, Loader2, ArrowLeft } from "lucide-react";

const COPY = {
  blog: {
    label: "Blog",
    heading: "From the Treasurescare blog",
    sub: "Guides, product deep-dives, and honest takes on device protection.",
  },
  news: {
    label: "News",
    heading: "News & press",
    sub: "Product launches, milestones, and where you'll find us next.",
  },
};

function fmtDate(iso) {
  try { return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }); }
  catch { return ""; }
}

export function PostList({ category }) {
  const [posts, setPosts] = useState(null);
  useEffect(() => {
    api.get("/content/posts", { params: { category } }).then((r) => setPosts(r.data));
  }, [category]);
  const copy = COPY[category] || COPY.blog;

  return (
    <PublicShell>
      <section className="max-w-6xl mx-auto px-6 lg:px-8 py-16">
        <div className="max-w-2xl">
          <div className="overline text-secondary">{copy.label}</div>
          <h1 className="mt-2 font-display text-5xl font-light tracking-tight">{copy.heading}</h1>
          <p className="mt-4 text-muted-foreground">{copy.sub}</p>
        </div>

        <div className="mt-12">
          {posts === null && (
            <div className="flex items-center gap-2 text-muted-foreground py-16">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          )}
          {posts && posts.length === 0 && (
            <div className="text-muted-foreground py-16">No {copy.label.toLowerCase()} posts yet.</div>
          )}
          {posts && posts.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid={`${category}-list`}>
              {posts.map((p) => (
                <Link
                  key={p.id}
                  to={`/${category}/${p.slug}`}
                  data-testid={`${category}-card-${p.slug}`}
                  className="group rounded-2xl border border-border bg-card overflow-hidden hover:border-primary/60 hover:shadow-teal-glow transition-all"
                >
                  {p.cover_photo ? (
                    <div className="aspect-[16/9] bg-muted overflow-hidden">
                      <img src={photoSrc(p.cover_photo, "thumb")} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform" loading="lazy" />
                    </div>
                  ) : (
                    <div className="aspect-[16/9] bg-gradient-to-br from-primary/20 to-secondary/20" />
                  )}
                  <div className="p-5">
                    {p.tags?.length > 0 && (
                      <div className="flex gap-1 flex-wrap mb-2">
                        {p.tags.slice(0, 3).map((t) => (
                          <span key={t} className="text-[10px] uppercase tracking-wider bg-muted text-muted-foreground px-2 py-0.5 rounded">{t}</span>
                        ))}
                      </div>
                    )}
                    <h3 className="font-display text-xl font-medium group-hover:text-primary transition-colors">{p.title}</h3>
                    {p.excerpt && <p className="mt-2 text-sm text-muted-foreground line-clamp-3">{p.excerpt}</p>}
                    <div className="mt-4 flex items-center gap-3 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {fmtDate(p.published_at || p.created_at)}</span>
                      {p.author_name && <span className="flex items-center gap-1"><UserIcon className="w-3 h-3" /> {p.author_name}</span>}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </PublicShell>
  );
}

export function PostDetail({ category }) {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);
  useEffect(() => {
    setPost(null); setNotFound(false);
    api.get(`/content/posts/${slug}`)
      .then((r) => setPost(r.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  const copy = COPY[category] || COPY.blog;

  return (
    <PublicShell>
      <article className="max-w-3xl mx-auto px-6 lg:px-8 py-16" data-testid="post-detail">
        <Link to={`/${category}`} className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to {copy.label}
        </Link>
        {notFound && <div className="mt-8 text-muted-foreground">Post not found.</div>}
        {post === null && !notFound && (
          <div className="mt-8 flex items-center gap-2 text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        )}
        {post && (
          <>
            <div className="mt-6 overline text-secondary">{copy.label}</div>
            <h1 className="mt-2 font-display text-4xl md:text-5xl font-light tracking-tight leading-tight">{post.title}</h1>
            <div className="mt-4 flex items-center gap-4 text-sm text-muted-foreground">
              <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {fmtDate(post.published_at || post.created_at)}</span>
              {post.author_name && <span className="flex items-center gap-1"><UserIcon className="w-3.5 h-3.5" /> {post.author_name}</span>}
            </div>
            {post.cover_photo && (
              <div className="mt-8 rounded-2xl overflow-hidden aspect-[16/9]">
                <img src={photoSrc(post.cover_photo)} alt={post.title} className="w-full h-full object-cover" />
              </div>
            )}
            <div
              className="mt-10 prose prose-invert max-w-none text-foreground/90 leading-relaxed [&_h3]:font-display [&_h3]:text-2xl [&_h3]:mt-8 [&_h3]:mb-3 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:my-3 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:my-3 [&_a]:text-primary [&_a:hover]:underline [&_p]:my-4"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(post.body_html) }}
            />
          </>
        )}
      </article>
    </PublicShell>
  );
}
